// Направление перехода между экранами — ОДНО правило на приложение.
//
// Зачем вообще. `viewTransition` стоит на ~280 ссылках, а правил оформления
// перехода в приложении не было ни одного, поэтому каждый переход отрабатывал
// так, как умеет браузер по умолчанию: снимок всего экрана, заморозка
// отрисовки, перекрёстное затухание — ОДИНАКОВОЕ вперёд и назад. Замер на
// Android 2026-08-27: заморозка 49–82 мс, полная длительность 325–365 мс.
// Затухание страницы целиком — самый узнаваемый признак веба: в нативных
// приложениях экран приезжает и уезжает, а вкладки переключаются мгновенно.
//
// Почему признак кладётся атрибутом на <html>, а не через `types` роутера.
// Роутер разрешает типы перехода только если `viewTransition` — ОБЪЕКТ:
//   const should = this.shouldViewTransition ?? this.options.defaultViewTransition
// Значение с самой ссылки перебивает роутерное умолчание, а в JSX-шорткате оно
// равно `true` — типы теряются. Отдать типы роутеру можно было бы, только сняв
// признак с 279 мест в 143 файлах; атрибут даёт тот же результат правкой трёх
// файлов. CSS выбирает анимацию по `html[data-nav-direction]`.
//
// Момент установки. Роутер шлёт `onBeforeLoad` ДО вызова `startViewTransition`
// (router-core/dist/esm/load-client.js: событие ~1042, переход ~970), поэтому
// к моменту снятия снимка атрибут уже стоит. Ставить позже — значит проиграть
// гонку с браузером и получить анимацию от прошлой навигации.

/** Куда идёт переход. `none` — двигать экран не нужно, подмена мгновенная. */
export type NavDirection =
	/** Углубление внутри одного каркаса — экран приезжает справа. */
	| "forward"
	/** Возврат внутри одного каркаса — экран уезжает вправо. */
	| "back"
	/** Экран заменяет каркас целиком — накрывает предыдущий снизу. */
	| "cover"
	/** Уход с такого экрана — он опускается вниз, открывая предыдущий. */
	| "uncover"
	/** Двигать нечего: подмена мгновенная. */
	| "none";

const ATTR = "data-nav-direction";

interface LocationLike {
	pathname: string;
	state?: { __TSR_index?: number } | undefined;
}

/**
 * Решение о направлении. Чистая функция — тестируется без роутера и без DOM.
 *
 * @param reduceMotion человек попросил систему уменьшить движение
 * @param coarsePointer управление пальцем (мобильный веб и приложение)
 */
export function resolveNavDirection(opts: {
	from: LocationLike | undefined;
	to: LocationLike;
	pathChanged: boolean;
	reduceMotion: boolean;
	coarsePointer: boolean;
	/** Пункт ли это меню. Приходит извне: реестр навигации живёт в компонентах,
	 *  а правило обязано оставаться чистым и проверяемым без них. */
	isFirstLevel: (pathname: string) => boolean;
	/** Прячет ли экран шапку и нижнюю навигацию (воркспейс курса, полный чат). */
	changesShell: (pathname: string) => boolean;
}): NavDirection {
	const { from, to, pathChanged, reduceMotion, coarsePointer, isFirstLevel, changesShell } = opts;

	// Первый рендер: ехать неоткуда.
	if (!from) return "none";
	// Уважение к настройке ОС идёт раньше всех прочих правил.
	if (reduceMotion) return "none";
	// На мыши и широком экране страницы не ездят — там ценится резкость.
	if (!coarsePointer) return "none";
	// Смена фильтра, открытие попапа, любой другой адрес того же экрана.
	// Двигать экран на это — врать пользователю про то, что он куда-то ушёл.
	if (!pathChanged) return "none";

	// Экран, заменяющий каркас, накрывает предыдущий, а не едет с ним бок о бок.
	// Иначе в кадре оказываются два каркаса сразу — две шапки и два нижних бара.
	const shellFrom = changesShell(from.pathname);
	const shellTo = changesShell(to.pathname);
	if (shellFrom !== shellTo) return shellTo ? "cover" : "uncover";

	const fromIndex = from.state?.__TSR_index ?? -1;
	const toIndex = to.state?.__TSR_index ?? 0;
	// Направление берётся из записи истории, а не из глубины пути. Глубина
	// обманывает: «назад» из списка в карточку (если пришли оттуда) обязан ехать
	// назад, хотя путь становится длиннее.
	if (toIndex < fromIndex) return "back";

	// Между пунктами меню экран НЕ едет — подменяется мгновенно. Движение
	// остаётся для углубления: открыть курс, документ, конспект. Решение
	// владельца 2026-08-27 после проверки на устройстве: на переключении
	// раздела анимация читается как задержка, а не как плавность.
	//
	// «Пункт меню» определяет реестр навигации, а не число сегментов пути:
	// `/library/folders` и `/courses/calendar` — тоже пункты, а `/materials/abc`
	// с тем же числом сегментов — уже углубление.
	if (isFirstLevel(from.pathname) && isFirstLevel(to.pathname)) return "none";

	return "forward";
}

/**
 * Умеет ли движок выбирать анимацию по типу перехода.
 *
 * Проверка та же, что делает сам роутер перед тем, как передать типы браузеру.
 * Если её не повторить и всё равно объявить умолчание, роутер молча забудет про
 * типы и включит собственный кроссфейд на КАЖДУЮ навигацию — включая те, где мы
 * просили не анимировать ничего.
 */
export function supportsViewTransitionTypes(): boolean {
	if (typeof window === "undefined") return false;
	if (typeof document === "undefined") return false;
	if (typeof document.startViewTransition !== "function") return false;
	return window.CSS?.supports?.("selector(:active-view-transition-type(a))") === true;
}

/**
 * Типы перехода для роутерного умолчания (`defaultViewTransition.types`).
 *
 * Нужны РОВНО ради одной способности, которой нет у атрибута: вернув `false`,
 * мы заставляем роутер вообще не звать `startViewTransition`
 * (router-core/dist/esm/router.js: `if (resolvedViewTransitionTypes === false)
 * return fn()`). Разница не косметическая — переход, даже ничего не
 * анимирующий, стоит заморозки отрисовки на 50–90 мс: браузер снимает снимок
 * экрана и не рисует, пока колбэк не отработает. Для смены фильтра и
 * переключения раздела это чистый убыток.
 *
 * Умолчание нужно потому, что возврат по истории ничьей ссылкой не
 * инициируется: без него «назад» оставался единственным переходом вообще без
 * движения. Значение с самой ссылки по-прежнему главнее (`?? `), и там, где
 * `viewTransition` проставлен вручную, анимацию по-прежнему выбирает CSS по
 * атрибуту — типы для этого не требуются.
 */
/**
 * Шов для роутера: обе точки подключения, связанные с реестром навигации.
 *
 * Предикат «пункт меню» приходит параметром, а не импортом: реестр живёт в
 * компонентах, а правило обязано оставаться чистым — иначе его нельзя
 * проверить без React и без дерева маршрутов.
 */
export function createNavTransitionSeam(
	isFirstLevel: (pathname: string) => boolean,
	changesShell: (pathname: string) => boolean,
): {
	stamp: (event: NavEvent) => NavDirection;
	types: (event: NavEvent) => string[] | false;
} {
	const stamp = (event: NavEvent): NavDirection => {
		if (typeof document === "undefined") return "none";

		const media = (query: string): boolean =>
			typeof window !== "undefined" && typeof window.matchMedia === "function"
				? window.matchMedia(query).matches
				: false;

		const direction = resolveNavDirection({
			from: event.fromLocation,
			to: event.toLocation,
			pathChanged: event.pathChanged,
			reduceMotion: media("(prefers-reduced-motion: reduce)"),
			coarsePointer: media("(pointer: coarse)"),
			isFirstLevel,
			changesShell,
		});

		document.documentElement.setAttribute(ATTR, direction);
		return direction;
	};

	return {
		stamp,
		/**
		 * Типы перехода для роутерного умолчания (`defaultViewTransition.types`).
		 *
		 * Нужны РОВНО ради одной способности, которой нет у атрибута: вернув
		 * `false`, мы заставляем роутер вообще не звать `startViewTransition`
		 * (router-core/dist/esm/router.js: `if (resolvedViewTransitionTypes ===
		 * false) return fn()`). Разница не косметическая — переход, даже ничего
		 * не анимирующий, стоит заморозки отрисовки на 50–90 мс: браузер снимает
		 * снимок экрана и не рисует, пока колбэк не отработает. Именно поэтому
		 * переключение раздела становится по-настоящему мгновенным, а не «без
		 * анимации, но с паузой».
		 *
		 * Умолчание нужно потому, что возврат по истории ничьей ссылкой не
		 * инициируется: без него «назад» оставался бы единственным переходом
		 * вообще без движения.
		 */
		types: (event: NavEvent) => {
			const direction = stamp(event);
			return direction === "none" ? false : [`sh-nav-${direction}`];
		},
	};
}

interface NavEvent {
	fromLocation?: LocationLike | undefined;
	toLocation: LocationLike;
	pathChanged: boolean;
}
